import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Badge, Button, Callout, Card, Loading, Mono, Screen } from '../../components/ui.js';
import { EmailField, PhoneField } from '../../components/contact-fields.js';
import { useNavReset } from '../../components/nav-reset.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { useSearchTyping } from '../../lib/typing-focus.js';
import { vendorMatches } from './vendor-find.js';
import { AddVendorDialog } from './add-vendor.js';
import './receiving.css';

/**
 * Vendors, as their own screen under Stock (revision item 25).
 *
 * Until now a vendor could be *created* from either picker and then never
 * seen again — no address, no phone, nowhere to fix a typo in an email an
 * order bounced off. This is where all of it is created and edited; the
 * printed purchase order and the receiving document put the address block
 * top-left, and this is where that block comes from.
 *
 * The code is displayed and never editable: it is the first segment of
 * historical SKU derivations and printed paper, and it cannot change once
 * anything is ordered (§18.1-3).
 */

type Vendor = Awaited<ReturnType<typeof api.purchasing.vendors.query>>[number];
type Carried = Awaited<ReturnType<typeof api.purchasing.vendorBrands.query>>;

export function VendorsScreen({ nav }: { nav?: import('react').ReactNode }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  // Typing anywhere on the screen lands in Find (the one search box).
  const searchRef = useSearchTyping(setSearch);
  const queryClient = useQueryClient();

  // Back to the list on a re-press of Vendors (item 31).
  useNavReset(() => {
    setSelectedId(null);
    setAdding(false);
  });

  const vendors = useQuery({
    queryKey: ['purchasing', 'vendors'],
    queryFn: () => api.purchasing.vendors.query(),
  });

  // The brands each vendor carries, from its models (decision 218).
  const carried = useQuery({
    queryKey: ['purchasing', 'vendorBrands'],
    queryFn: () => api.purchasing.vendorBrands.query(),
    staleTime: 60_000,
  });
  const brandsOf = (vendorId: string) =>
    (carried.data ?? []).filter((entry) => entry.vendorId === vendorId);

  const selected = (vendors.data ?? []).find((vendor) => vendor.id === selectedId) ?? null;
  const shown = (vendors.data ?? []).filter((vendor) =>
    vendorMatches(
      vendor,
      search,
      brandsOf(vendor.id).map((entry) => entry.brandName),
    ),
  );

  return (
    <Screen
      title="Vendors"
      subtitle="Who the shop buys from — and the address and phone the paperwork prints."
      width="wide"
      primaryAction={
        <Button variant="primary" onClick={() => setAdding(true)}>
          New vendor
        </Button>
      }
    >
      {nav}
      {vendors.isError ? <Callout tone="danger">{errorMessage(vendors.error)}</Callout> : null}

      {adding ? (
        <AddVendorDialog
          onClose={() => setAdding(false)}
          onCreated={(vendor) => {
            setAdding(false);
            void queryClient.invalidateQueries({ queryKey: ['purchasing', 'vendors'] });
            setSelectedId(vendor.id);
          }}
        />
      ) : null}

      <div className="products">
        <div className="products__list">
          {vendors.isPending ? (
            <Loading label="Loading vendors" />
          ) : (
            <Card padded={false}>
              <div className="vendors__find">
                <input
                  className="input"
                  ref={searchRef}
                  placeholder="Find a vendor — name, code, account #, contact, city or brand"
                  aria-label="Find a vendor"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              {shown.length === 0 ? (
                <p className="text-secondary vendors__empty">
                  {search.trim() === '' ? 'No vendors yet.' : 'No vendor matches that.'}
                </p>
              ) : null}
              <ul className="product-results">
                {shown.map((vendor) => (
                  <li key={vendor.id}>
                    <button
                      type="button"
                      className={`product-result${vendor.id === selectedId ? ' product-result--active' : ''}`}
                      onClick={() => setSelectedId(vendor.id)}
                    >
                      <span className="product-result__name">
                        {vendor.name}
                        {!vendor.active ? <Badge tone="warning">Inactive</Badge> : null}
                        {/* The paperwork prints an empty corner until this is
                            filled — worth a marker, like an unfinished style. */}
                        {vendor.addressLine1 === null ? (
                          <Badge tone="info">No address</Badge>
                        ) : null}
                      </span>
                      <span className="product-result__meta">
                        <Mono>{vendor.code}</Mono>
                        {vendor.phone !== null ? ` · ${vendor.phone}` : ''}
                        {vendor.city !== null ? ` · ${vendor.city}` : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        <div className="products__detail">
          {selected === null ? (
            <Card>
              <p className="text-secondary">Pick a vendor to see and edit everything on file.</p>
            </Card>
          ) : (
            <VendorDetail
              key={selected.id}
              vendor={selected}
              carried={brandsOf(selected.id)}
              onSaved={() =>
                void queryClient.invalidateQueries({ queryKey: ['purchasing', 'vendors'] })
              }
            />
          )}
        </div>
      </div>
    </Screen>
  );
}

function VendorDetail({
  vendor,
  carried,
  onSaved,
}: {
  vendor: Vendor;
  carried: Carried;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(() => ({
    name: vendor.name,
    accountNumber: vendor.accountNumber ?? '',
    terms: vendor.terms ?? '',
    contactName: vendor.contactName ?? '',
    email: vendor.email ?? '',
    phone: vendor.phone ?? '',
    addressLine1: vendor.addressLine1 ?? '',
    addressLine2: vendor.addressLine2 ?? '',
    city: vendor.city ?? '',
    region: vendor.region ?? '',
    postalCode: vendor.postalCode ?? '',
    notes: vendor.notes ?? '',
    active: vendor.active,
  }));
  const [saved, setSaved] = useState(false);

  // The confirmation is for *this* save; a new edit stands it down.
  useEffect(() => setSaved(false), [draft]);

  const blank = (value: string) => (value.trim() === '' ? null : value.trim());

  const save = useMutation({
    mutationFn: () =>
      api.purchasing.updateVendor.mutate({
        vendorId: vendor.id,
        name: draft.name.trim(),
        accountNumber: blank(draft.accountNumber),
        terms: blank(draft.terms),
        contactName: blank(draft.contactName),
        email: blank(draft.email),
        phone: blank(draft.phone),
        addressLine1: blank(draft.addressLine1),
        addressLine2: blank(draft.addressLine2),
        city: blank(draft.city),
        region: blank(draft.region),
        postalCode: blank(draft.postalCode),
        notes: blank(draft.notes),
        active: draft.active,
      }),
    onSuccess: () => {
      recordBreadcrumb('purchasing', `edited vendor ${draft.name}`);
      setSaved(true);
      onSaved();
    },
  });

  const field = (
    label: string,
    key: keyof typeof draft & string,
    options: { maxLength?: number } = {},
  ) => (
    <div className="field">
      <label className="field__label" htmlFor={`vendor-${key}`}>
        {label}
      </label>
      <input
        id={`vendor-${key}`}
        className="input"
        maxLength={options.maxLength ?? 200}
        value={draft[key] as string}
        onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
      />
    </div>
  );

  return (
    <Card
      title={vendor.name}
      actions={
        <div className="style__actions">
          <Button
            variant="primary"
            busy={save.isPending}
            disabledReason={draft.name.trim() === '' ? 'A vendor needs a name' : undefined}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </div>
      }
    >
      {save.isError ? <Callout tone="danger">{errorMessage(save.error)}</Callout> : null}
      {saved ? <Callout tone="success">Saved.</Callout> : null}

      {/* Read from its models: a vendor that owns several brands shows them all. */}
      <p className="vendor__brands">
        <span className="label-micro">Brands carried</span>{' '}
        {carried.length === 0
          ? 'None yet — no active model names this vendor.'
          : carried
              .map((entry) => `${entry.brandName} ${entry.models.toLocaleString()}`)
              .join(' · ')}
      </p>

      <div className="style-form">
        {field('Name', 'name', { maxLength: 120 })}
        <div className="field">
          <label className="field__label" htmlFor="vendor-code-shown">
            Code
          </label>
          <input id="vendor-code-shown" className="input code" value={vendor.code} disabled />
          <p className="field__hint">
            The first segment of SKUs their goods carried — printed on labels, so it never changes.
          </p>
        </div>
        {field('Account number', 'accountNumber', { maxLength: 60 })}
        {field('Payment terms', 'terms', { maxLength: 120 })}
        {field('Contact', 'contactName', { maxLength: 120 })}
      </div>

      <div className="style-form">
        <EmailField
          label="Email"
          hint="Where their orders are sent"
          value={draft.email}
          onChange={(value) => setDraft({ ...draft, email: value })}
          suggestDomains={false}
        />
        <PhoneField
          value={draft.phone}
          onChange={(value) => setDraft({ ...draft, phone: value })}
        />
      </div>

      <div className="style-form">
        {field('Street address', 'addressLine1')}
        {field('Address line 2', 'addressLine2')}
        {field('City', 'city', { maxLength: 100 })}
        {field('State', 'region', { maxLength: 100 })}
        {field('ZIP', 'postalCode', { maxLength: 20 })}
      </div>

      <div className="field">
        <label className="field__label" htmlFor="vendor-notes">
          Notes
        </label>
        <textarea
          id="vendor-notes"
          className="input textarea"
          rows={2}
          maxLength={1000}
          value={draft.notes}
          onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
        />
      </div>

      <label className="toggle">
        <input
          type="checkbox"
          checked={!draft.active}
          onChange={(event) => setDraft({ ...draft, active: !event.target.checked })}
        />
        <span>
          Inactive — out of both pickers, with their history and models left intact. For a supplier
          the shop no longer buys from.
        </span>
      </label>
    </Card>
  );
}
